/**
 * Music bus: one loop set, one source per loop, on one clock, mixed by preset.
 *
 * Every loop in the set is decoded once, started together once all are
 * ready (so voices never drift out of phase), and looped to the shortest
 * common length. Moods ("stems") are mix presets over those loops
 * (audio/musicMix.ts); the conductor sets stem weights and the manager ramps
 * each loop's gain to the normalised blend. A synthesized heartbeat loop is
 * an extra voice in every set, used by the low-health preset.
 *
 * 67.22: at each loop boundary the manager plans the next loop (`nextLoopPlan`): which loop of each variant family
 * plays, and in calm whether the music rests for one loop; the change lands on the seam (all loops run in sync, so a
 * swap is a gain change, never a restart). A rest ends at once if the fight brings tension in.
 */

import { getAudioContext, getMusicBus } from './sfx';
import {
  DEFAULT_MUSIC_SET, HEARTBEAT_LOOP, MUSIC_SETS, loopIdsOf, mixLoopGains, renderHeartbeat, nextLoopPlan, restAllowed,
  type MusicSet, type MusicSetId, type MusicStemId, type LoopPlan,
} from './musicMix';

export type { MusicStemId, MusicSetId } from './musicMix';

const MUSIC_SET_KEY = 'orbituary-music-set';

export function loadMusicSetId(): MusicSetId {
  try {
    const v = localStorage.getItem(MUSIC_SET_KEY);
    if (v === 'composed' || v === 'original') return v;   // the Settings choice (ground is the action clock's, picked per match)
  } catch { /* storage unavailable */ }
  return DEFAULT_MUSIC_SET;
}

export function saveMusicSetId(id: MusicSetId): void {
  try { localStorage.setItem(MUSIC_SET_KEY, id); } catch { /* storage unavailable */ }
}

const bufferCache = new Map<string, Promise<AudioBuffer>>();
function loadAudioBuffer(ctx: AudioContext, url: string): Promise<AudioBuffer> {
  let p = bufferCache.get(url);
  if (!p) {
    p = fetch(url).then(r => r.arrayBuffer()).then(ab => ctx.decodeAudioData(ab));
    bufferCache.set(url, p);
  }
  return p;
}

interface LoopVoice {
  gain: GainNode;
  source: AudioBufferSourceNode | null;
}

export class MusicManager {
  readonly set: MusicSet;
  private ctx: AudioContext;
  private voices = new Map<string, LoopVoice>();
  private weights: Partial<Record<MusicStemId, number>> = {};
  private started = false;
  private disposed = false;
  /** 67.22: this loop's variants and rest; the loop clock (its start and length) and the boundary timer */
  private plan: LoopPlan = { active: {}, resting: false };
  private loopStart = 0;
  private loopLength = 0;
  private boundaryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(set: MusicSet = MUSIC_SETS[loadMusicSetId()]) {
    const ctx = getAudioContext();
    const bus = getMusicBus();
    if (!ctx || !bus) throw new Error('Audio context not initialized — call initAudio() first');
    this.ctx = ctx;
    this.set = set;
    for (const id of loopIdsOf(set)) {
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(bus);
      this.voices.set(id, { gain, source: null });
    }
    // A browser that cannot decode the loops (Safari has no Ogg Vorbis in decodeAudioData) plays
    // the match without music; an unhandled rejection here would raise the global error screen.
    this.startAll().catch(e => console.warn('Music unavailable:', e instanceof Error ? e.message : e));
  }

  /** Decode every loop, then start them all on the same tick, looping to the shortest length. */
  private async startAll(): Promise<void> {
    const ctx = this.ctx;
    const sampled = await Promise.all(
      Object.entries(this.set.loops).map(async ([id, url]) => [id, await loadAudioBuffer(ctx, url)] as const),
    );
    if (this.disposed) return;

    const hbSamples = renderHeartbeat(ctx.sampleRate);
    const hb = ctx.createBuffer(1, hbSamples.length, ctx.sampleRate);
    hb.getChannelData(0).set(hbSamples);

    const shortest = Math.min(...sampled.map(([, b]) => b.duration));
    const t0 = ctx.currentTime + 0.05;
    for (const [id, buffer] of sampled) {
      const v = this.voices.get(id)!;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.loopStart = 0;
      src.loopEnd = shortest;          // one clock: every loop wraps at the same moment
      src.connect(v.gain);
      src.start(t0);
      v.source = src;
    }
    {
      const v = this.voices.get(HEARTBEAT_LOOP)!;
      const src = ctx.createBufferSource();
      src.buffer = hb;
      src.loop = true;
      src.connect(v.gain);
      src.start(t0);
      v.source = src;
    }
    this.started = true;
    this.loopStart = t0;
    this.loopLength = shortest;
    this.applyMix(1.5);
    if (this.set.variants || this.set.rests) this.scheduleBoundary();
  }

  /** 67.22: a quarter second before the next loop boundary, plan that loop and land the change on the seam. */
  private scheduleBoundary(): void {
    if (this.disposed || this.loopLength <= 0) return;
    const now = this.ctx.currentTime;
    const k = Math.floor((now - this.loopStart) / this.loopLength) + 1;
    const seam = this.loopStart + k * this.loopLength;
    this.boundaryTimer = setTimeout(() => {
      if (this.disposed) return;
      this.plan = nextLoopPlan(this.set, this.weights, this.plan, Math.random);
      this.applyMix(this.plan.resting ? 2.0 : 0.15, seam);
      this.scheduleBoundary();
    }, Math.max(0, (seam - now - 0.25) * 1000));
  }

  /** Ramp every loop to the normalised blend of the current stem weights (from `at`, default now — 67.22: a seam). */
  private applyMix(rampSeconds: number, at?: number): void {
    const gains = mixLoopGains(this.weights, this.set, this.plan);
    const now = this.ctx.currentTime;
    if (at !== undefined && at > now) {
      for (const [id, v] of this.voices) {
        v.gain.gain.cancelScheduledValues(at);
        v.gain.gain.setTargetAtTime(gains[id] ?? 0, at, Math.max(0.01, rampSeconds / 3));
      }
      return;
    }
    for (const [id, v] of this.voices) {
      v.gain.gain.cancelScheduledValues(now);
      v.gain.gain.setValueAtTime(v.gain.gain.value, now);
      v.gain.gain.linearRampToValueAtTime(gains[id] ?? 0, now + rampSeconds);
    }
  }

  /** Set a stem's weight; the loops ramp to the new blend over `rampSeconds`. */
  setStemGain(stemId: MusicStemId, target: number, rampSeconds = 1.5): void {
    this.weights[stemId] = Math.max(0, target);
    if (this.plan.resting && !restAllowed(this.weights)) this.plan = { ...this.plan, resting: false };   // 67.22: the fight ends a rest
    if (this.started) this.applyMix(rampSeconds);
  }

  /** One stem at full weight, the rest silent. */
  solo(stemId: MusicStemId, rampSeconds = 1.5): void {
    this.weights = { [stemId]: 1 };
    if (this.started) this.applyMix(rampSeconds);
  }

  cleanup(): void {
    this.disposed = true;
    if (this.boundaryTimer) clearTimeout(this.boundaryTimer);
    for (const v of this.voices.values()) {
      try { v.source?.stop(); } catch { /* not started */ }
      v.source?.disconnect();
      v.gain.disconnect();
    }
    this.voices.clear();
  }
}
